import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { trackTour } from '../lib/analytics';
import { CalendarClock, MessagesSquare, Settings, Sparkles, ArrowRight, ArrowLeft, X } from 'lucide-react';

const STEPS = [
  { icon: Sparkles, title: 'Welcome to Larkwell! \ud83c\udf31', body: "A quick 20-second tour of the three places you'll use most. No pressure \u2014 you can skip anytime.", cta: 'Show me around' },
  { icon: CalendarClock, title: 'Today \u2014 your gentle plan', body: 'Turn a heavy day into a visual, break-friendly schedule. Tap \u201cPlan My Day\u201d, watch your Capacity Meter, and start a Focus sprint when ready.', cta: 'Next' },
  { icon: MessagesSquare, title: 'Chats \u2014 your AI coach', body: 'Talk things through 1:1 or in a group with AI agents. Use voice to dictate, hear replies read aloud, or open a hands-free Call.', cta: 'Next' },
  { icon: Settings, title: 'Settings \u2014 make it yours', body: 'Low-stim mode, dyslexia-friendly font, text size, and light/dark themes. Connect your calendar and messaging here too.', cta: 'Start with Today' },
];

export const GuidedTour = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const key = user ? `larkwell_tour_done_${user.user_id}` : 'larkwell_tour_done';
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(key) !== '1'; } catch (e) { return false; }
  });
  const [i, setI] = useState(0);

  useEffect(() => {
    const replay = () => { setI(0); setOpen(true); };
    window.addEventListener('larkwell:replay-tour', replay);
    return () => window.removeEventListener('larkwell:replay-tour', replay);
  }, []);

  // Log when the tour is shown (first-run or replay).
  useEffect(() => {
    if (open && user) trackTour('guided', 'shown', 'firstrun');
  }, [open, user]);

  if (!open || !user) return null;

  const finish = (completed = false) => {
    try { localStorage.setItem(key, '1'); } catch (e) { console.debug('tour persist failed', e); }
    trackTour('guided', completed ? 'completed' : 'skipped', 'firstrun');
    setOpen(false);
    try { window.dispatchEvent(new Event('larkwell:tour-finished')); } catch (e) {}
  };
  const next = () => {
    if (i < STEPS.length - 1) setI(i + 1);
    else { finish(true); navigate('/app/today'); }
  };

  const step = STEPS[i];
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-[200] grid place-items-center p-4" data-testid="guided-tour" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => finish(false)} />
      <div className="relative w-full max-w-md rounded-2xl border border-[hsl(var(--border))] bg-card p-6 shadow-xl">
        <button onClick={() => finish(false)} aria-label="Skip tour" className="absolute top-3 right-3 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="tour-skip"><X className="h-4 w-4" /></button>
        <div className="h-12 w-12 rounded-2xl grid place-items-center mb-4" style={{ background: 'hsl(var(--primary))' }}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <h2 className="font-display text-xl font-700" data-testid="tour-title">{step.title}</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed">{step.body}</p>

        <div className="flex items-center gap-1.5 mt-5" aria-hidden="true">
          {STEPS.map((s, idx) => (
            <span key={s.title} className="h-1.5 rounded-full transition-all" style={{ width: idx === i ? 22 : 7, background: idx === i ? 'hsl(var(--primary))' : 'hsl(var(--border))' }} />
          ))}
        </div>

        <div className="flex items-center justify-between mt-5">
          {i > 0 ? (
            <Button variant="ghost" onClick={() => setI(i - 1)} className="rounded-xl gap-1 text-[hsl(var(--muted-foreground))]" data-testid="tour-back"><ArrowLeft className="h-4 w-4" /> Back</Button>
          ) : (
            <button onClick={() => finish(false)} className="text-sm text-[hsl(var(--muted-foreground))] hover:underline" data-testid="tour-skip-text">Skip tour</button>
          )}
          <Button onClick={next} className="rounded-xl gap-1.5" style={{ background: 'hsl(var(--primary))' }} data-testid="tour-next">{step.cta} <ArrowRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
